import { AddRounded, RemoveRounded } from '@mui/icons-material'
import { Box, Grid, IconButton, IconButtonOwnProps, InputBase, Typography } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

export type Props = {
    price: number
    setTargetPrice: (price: number) => void
    tickSize?: number
    minStrike?: number
    maxStrike?: number
}

const TargetPriceInput = ({ price, setTargetPrice, tickSize = 5, minStrike = 0, maxStrike = Infinity }: Props) => {
    const [inputValue, setInputValue] = useState<string>(price?.toFixed(2) ?? '0')

    const roundToTickSize = useCallback((value: number): number => {
        if (!tickSize || tickSize <= 0) return value
        return Math.round(value / tickSize) * tickSize
    }, [tickSize])

    const clampValue = useCallback((value: number): number => {
        return Math.max(minStrike, Math.min(maxStrike, value))
    }, [minStrike, maxStrike])

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setInputValue(value)
        }
    }, [])

    const handleInputBlur = useCallback(() => {
        const numValue = parseFloat(inputValue)

        if (isNaN(numValue) || inputValue === '') {
            setInputValue(price?.toFixed(2) ?? '0')
            return
        }

        const roundedValue = roundToTickSize(numValue)
        const clampedValue = clampValue(roundedValue)
        setInputValue(clampedValue.toFixed(2))

        if (clampedValue !== price) {
            setTargetPrice(clampedValue)
        }
    }, [inputValue, price, roundToTickSize, clampValue, setTargetPrice])

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur()
        }
    }, [])

    useEffect(() => {
        setInputValue(price?.toFixed(2) ?? '0')
    }, [price])

    const plusClick = useCallback(() => {
        const newValue = price + 10
        const roundedValue = roundToTickSize(newValue)
        const clampedValue = clampValue(roundedValue)
        setTargetPrice(clampedValue)
    }, [price, setTargetPrice, roundToTickSize, clampValue])

    const minusClick = useCallback(() => {
        const newValue = price - 10
        const roundedValue = roundToTickSize(newValue)
        const clampedValue = clampValue(roundedValue)
        setTargetPrice(clampedValue)
    }, [price, setTargetPrice, roundToTickSize, clampValue])

    const minusBtnColor: IconButtonOwnProps["color"] = useMemo(() => {
        const newValue = roundToTickSize(price - 10)
        return newValue >= minStrike ? 'inherit' : undefined
    }, [price, minStrike, roundToTickSize])

    const minusDisabled = useMemo(() => {
        const newValue = roundToTickSize(price - 10)
        return newValue < minStrike
    }, [price, minStrike, roundToTickSize])

    const plusDisabled = useMemo(() => {
        const newValue = roundToTickSize(price + 10)
        return newValue > maxStrike
    }, [price, maxStrike, roundToTickSize])

    const priceText = useMemo(
        () => (
            <Typography sx={{ fontSize: '10px' }}>
                SPOT PRICE
            </Typography>
        ),
        []
    )
    const minusIcon = useMemo(() => <RemoveRounded className="trade-panel-edit-icons" />, [])
    const plusIcon = useMemo(() => <AddRounded className="trade-panel-edit-icons" />, [])

    const minusButton = useMemo(() => {
        return (
            <IconButton
                data-event={'Spot Price Changed'} data-event-source={'strategy-builder-submenu'} data-event-type={'decrement'}
                className="order-buttons"
                sx={{ padding: '5px', cursor: 'pointer', width: '24px', height: '24px' }}
                color={minusBtnColor}
                disabled={minusDisabled}
                onClick={minusClick}
            >
                {minusIcon}
            </IconButton>
        )
    }, [minusBtnColor, minusDisabled, minusClick, minusIcon])

    const plusButton = useMemo(() => {
        return (
            <IconButton 
                data-event={'Spot Price Changed'} 
                data-event-source={'strategy-builder-submenu'} 
                data-event-type={'increment'} 
                className="order-buttons" 
                style={{ width: '24px', height: '24px' }} 
                sx={{ padding: '5px', width: '24px', height: '24px', cursor: 'pointer' }} 
                color={'inherit'} 
                onClick={plusClick}
                disabled={plusDisabled}
            >
                {plusIcon}
            </IconButton>
        )
    }, [plusClick, plusIcon, plusDisabled])

    const buttonsView = useMemo(() => {
        return (
            <Box
                component="span"
                sx={{
                    height: '24px',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                {minusButton}
                <InputBase
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    className="order-custom-input"
                    sx={{
                        height: '100%',
                        textAlign: 'center',
                        backgroundColor: 'none',
                        width: '80px',
                        fontSize: '14px',
                        color: 'inherit',
                        '& .MuiInputBase-input': { padding: '4px 0 5px', textAlign: 'center' }
                    }}
                />
                {plusButton}
            </Box>
        )
    }, [minusButton, plusButton, inputValue, handleInputChange, handleInputBlur, handleKeyDown])

    return  <Grid item id={'iv-multiplier-input'} sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                <Grid item xs={12} textAlign={'left'}>
                    <Typography fontSize={10} component="span" color={'text.secondary'} className="trade-panel-text">
                        {priceText}
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography className="trade-panel-boldest-text" variant="body2" component="span" color="primaryText" fontSize={'small'} sx={{ fontSize: '10px' }}>
                        {buttonsView}
                    </Typography>
                </Grid>
                {/* <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', transform: 'translateX(40%)' }}> */}
            </Grid>
}

export default memo(TargetPriceInput)
